import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const EmergencyBookingWidget = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [urgencyLevel, setUrgencyLevel] = useState('high');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    serviceType: '',
    description: ''
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const urgencyOptions = [
    { value: 'high', label: 'High Priority (2-4 hours)' },
    { value: 'emergency', label: 'Emergency (Within 1 hour)' },
    { value: 'critical', label: 'Critical (Immediate response)' }
  ];

  const serviceOptions = [
    { value: 'water-damage', label: 'Water Damage Cleanup' },
    { value: 'fire-damage', label: 'Fire/Smoke Damage' },
    { value: 'biohazard', label: 'Biohazard Cleanup' },
    { value: 'flood-cleanup', label: 'Flood Cleanup' },
    { value: 'mold-removal', label: 'Emergency Mold Removal' },
    { value: 'deep-sanitization', label: 'Deep Sanitization' },
    { value: 'other', label: 'Other Emergency' }
  ];

  const getSurgePricing = () => {
    const hour = currentTime?.getHours();
    const isWeekend = currentTime?.getDay() === 0 || currentTime?.getDay() === 6;
    const isNightTime = hour < 6 || hour > 22;

    if (urgencyLevel === 'critical') return 2.5;
    if (urgencyLevel === 'emergency') return 2.0;
    if (isNightTime || isWeekend) return 1.5;
    return 1.0;
  };

  const getSurgeText = () => {
    const multiplier = getSurgePricing();
    if (multiplier === 1.0) return "Standard rates apply";
    return `${Math.round((multiplier - 1) * 100)}% surge pricing`;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEmergencyCall = () => {
    window.location.href = 'tel:+15551234567';
  };

  const handleWhatsAppEmergency = () => {
    const message = encodeURIComponent(
      `🚨 EMERGENCY SERVICE REQUEST\nUrgency: ${urgencyLevel}\nService: ${formData?.serviceType}\nLocation: ${formData?.address}\nDescription: ${formData?.description}`
    );
    window.open(`https://wa.me/15551234567?text=${message}`, '_blank');
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    console.log('Emergency booking submitted:', { ...formData, urgencyLevel, surgePricing: getSurgePricing() });
    alert('Emergency request submitted! We will contact you immediately.');
    setIsExpanded(false);
  };

  return (
    <section className="py-16 lg:py-24 bg-error/5 border-t-4 border-error">
      <div className="container mx-auto px-4 lg:px-6">
        <div className="max-w-4xl mx-auto">
          {/* Emergency Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-error text-white px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Icon name="AlertTriangle" size={16} />
              <span>24/7 Emergency Service Available</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary mb-6">
              Need Emergency Cleaning?
            </h2>
            <p className="text-xl text-text-secondary max-w-3xl mx-auto">
              Water damage, fire cleanup, biohazard situations - we respond immediately 
              with professional emergency cleaning services when you need us most.
            </p>
          </div>

          {/* Emergency Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <div className="text-center bg-card rounded-lg p-6 shadow-elevation-1">
              <div className="text-2xl font-bold text-error mb-2">
                &lt;30min
              </div>
              <div className="text-sm text-text-secondary">Response Time</div>
            </div>
            <div className="text-center bg-card rounded-lg p-6 shadow-elevation-1">
              <div className="text-2xl font-bold text-error mb-2">24/7</div>
              <div className="text-sm text-text-secondary">Availability</div>
            </div>
            <div className="text-center bg-card rounded-lg p-6 shadow-elevation-1">
              <div className="text-2xl font-bold text-error mb-2">Licensed</div>
              <div className="text-sm text-text-secondary">& Insured</div>
            </div>
            <div className="text-center bg-card rounded-lg p-6 shadow-elevation-1">
              <div className="text-2xl font-bold text-error mb-2">
                {currentTime?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
              <div className="text-sm text-text-secondary">Current Time</div>
            </div>
          </div>

          {/* Quick Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button
              variant="destructive"
              size="lg"
              onClick={handleEmergencyCall}
              iconName="Phone"
              iconPosition="left"
              className="text-lg px-8 py-4 animate-pulse"
            >
              Emergency Call Now
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={handleWhatsAppEmergency}
              iconName="MessageSquare"
              iconPosition="left"
              className="text-lg px-8 py-4 border-success text-success hover:bg-success hover:text-white"
            >
              WhatsApp Emergency
            </Button>
            <Button
              variant="secondary"
              size="lg"
              onClick={() => setIsExpanded(!isExpanded)}
              iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
              iconPosition="right"
              className="text-lg px-8 py-4"
            >
              Quick Booking Form
            </Button>
          </div>

          {/* Expandable Booking Form */}
          {isExpanded && (
            <div className="bg-card rounded-xl p-8 shadow-elevation-3 border border-border">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-text-primary">
                  Emergency Service Request
                </h3>
                <div className="text-sm text-text-secondary">
                  {getSurgeText()}
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Full Name"
                    type="text"
                    required
                    value={formData?.name}
                    onChange={(e) => handleInputChange('name', e?.target?.value)}
                    placeholder="Enter your name"
                  />
                  <Input
                    label="Phone Number"
                    type="tel"
                    required
                    value={formData?.phone}
                    onChange={(e) => handleInputChange('phone', e?.target?.value)}
                    placeholder="Emergency contact number"
                  />
                </div>

                <Input
                  label="Emergency Location"
                  type="text"
                  required
                  value={formData?.address}
                  onChange={(e) => handleInputChange('address', e?.target?.value)}
                  placeholder="Full address where service is needed"
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Select
                    label="Urgency Level"
                    options={urgencyOptions}
                    value={urgencyLevel}
                    onChange={setUrgencyLevel}
                    required
                  />
                  <Select
                    label="Service Type"
                    options={serviceOptions}
                    value={formData?.serviceType}
                    onChange={(value) => handleInputChange('serviceType', value)}
                    placeholder="Select emergency service"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Emergency Description
                  </label>
                  <textarea
                    rows={4}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none text-sm"
                    placeholder="Describe the emergency situation in detail..."
                    value={formData?.description}
                    onChange={(e) => handleInputChange('description', e?.target?.value)}
                    required
                  />
                </div>

                {/* Pricing Notice */}
                <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="Info" size={20} className="text-warning flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <div className="font-medium text-warning mb-1">Pricing Notice</div>
                      <div className="text-text-secondary">
                        Emergency services include surge pricing based on urgency level and time of day. 
                        Current multiplier: <strong>{getSurgePricing()}x</strong>. 
                        Final pricing will be confirmed before service begins.
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    type="submit"
                    variant="destructive"
                    iconName="Send"
                    iconPosition="left"
                    className="flex-1"
                  >
                    Submit Emergency Request
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsExpanded(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          )}

          {/* Emergency Services List */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {[
              { icon: "Droplets", title: "Water Damage", description: "Flood cleanup, pipe bursts, water extraction" },
              { icon: "Flame", title: "Fire/Smoke Damage", description: "Soot removal, odor elimination, restoration" },
              { icon: "Shield", title: "Biohazard Cleanup", description: "Crime scenes, medical waste, contamination" },
              { icon: "Wind", title: "Storm Damage", description: "Weather-related cleanup and restoration" },
              { icon: "Zap", title: "Mold Emergency", description: "Immediate mold containment and removal" },
              { icon: "Sparkles", title: "Deep Sanitization", description: "Virus, bacteria, and pathogen elimination" }
            ]?.map((service, index) => (
              <div key={index} className="bg-card rounded-lg p-6 border border-border hover:border-error/50 transition-colors duration-200">
                <div className="w-12 h-12 bg-error/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name={service?.icon} size={24} className="text-error" />
                </div>
                <h4 className="text-lg font-semibold text-text-primary mb-2">
                  {service?.title}
                </h4>
                <p className="text-text-secondary text-sm">
                  {service?.description}
                </p>
              </div>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="text-center mt-12">
            <p className="text-text-secondary mb-6">
              For non-emergency services, explore our regular cleaning and flooring options.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/service-details">
                <Button
                  variant="default"
                  size="lg"
                  iconName="Wrench"
                  iconPosition="left"
                >
                  Regular Services
                </Button>
              </Link>
              <Link to="/smart-contact">
                <Button
                  variant="outline"
                  size="lg"
                  iconName="Calendar"
                  iconPosition="left"
                >
                  Schedule Consultation
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EmergencyBookingWidget;